import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Plus, 
  Trash2,
  ArrowLeft,
  Zap,
  Users,
  Calendar,
  CheckCircle,
  Loader2
} from 'lucide-react';

export default function BulkGeneratePage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedInvoices, setGeneratedInvoices] = useState([]);
  
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [formData, setFormData] = useState({
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, rate: 0 }],
    tax_rate: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, templatesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/templates')
      ]);
      setClients(clientsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleClientToggle = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const selectAllClients = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, rate: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
    }, 0);
    const tax = subtotal * ((parseFloat(formData.tax_rate) || 0) / 100);
    return subtotal + tax;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleGenerate = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }

    if (formData.items.some(item => !item.description || !item.rate)) {
      toast.error('Please fill in all item details');
      return;
    }

    setGenerating(true);
    setGeneratedInvoices([]);

    try {
      const results = [];
      
      for (const clientId of selectedClients) {
        try {
          const payload = {
            client_id: clientId,
            template_id: selectedTemplate || null,
            issue_date: formData.issue_date,
            due_date: formData.due_date,
            items: formData.items.map(item => ({
              description: item.description,
              quantity: parseFloat(item.quantity) || 1,
              rate: parseFloat(item.rate) || 0
            })),
            tax_rate: parseFloat(formData.tax_rate) || 0,
            notes: formData.notes
          };

          const response = await api.post('/invoices', payload);
          results.push({ 
            success: true, 
            invoice: response.data,
            client: clients.find(c => c.id === clientId)
          });
        } catch (error) {
          results.push({ 
            success: false, 
            error: error.response?.data?.detail || 'Failed',
            client: clients.find(c => c.id === clientId)
          });
        }
      }

      setGeneratedInvoices(results);
      
      const successCount = results.filter(r => r.success).length;
      if (successCount === results.length) {
        toast.success(`Successfully generated ${successCount} invoices!`);
      } else {
        toast.warning(`Generated ${successCount}/${results.length} invoices`);
      }
    } catch (error) {
      toast.error('Bulk generation failed');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bulk-generate-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/invoices')}
            className="h-9 w-9 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Zap className="w-7 h-7 text-[#4F46E5]" />
              Bulk Generate Invoices
            </h1>
            <p className="page-subtitle">
              Generate invoices for multiple clients at once
            </p>
          </div>
        </div>
      </div>

      {generatedInvoices.length > 0 ? (
        /* Results View */
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Generation Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 mb-6">
              {generatedInvoices.map((result, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    result.success ? 'bg-emerald-50' : 'bg-rose-50'
                  }`}
                >
                  <div>
                    <p className="font-medium">{result.client?.name}</p>
                    {result.success ? (
                      <p className="text-sm text-emerald-600">
                        Invoice #{result.invoice.invoice_number} - {formatCurrency(result.invoice.total)}
                      </p>
                    ) : (
                      <p className="text-sm text-rose-600">{result.error}</p>
                    )}
                  </div>
                  {result.success && (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => navigate('/invoices')}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              >
                View All Invoices
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setGeneratedInvoices([]);
                  setSelectedClients([]);
                }}
              >
                Generate More
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Form View */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Clients */}
          <Card className="dashboard-card lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#4F46E5]" />
                  Select Clients
                </span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={selectAllClients}
                  className="text-xs"
                >
                  {selectedClients.length === clients.length ? 'Deselect All' : 'Select All'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clients.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {clients.map((client) => (
                    <div 
                      key={client.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedClients.includes(client.id) 
                          ? 'bg-[#4F46E5]/10 border border-[#4F46E5]/30' 
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      onClick={() => handleClientToggle(client.id)}
                      data-testid={`client-checkbox-${client.id}`}
                    >
                      <Checkbox 
                        checked={selectedClients.includes(client.id)}
                        onCheckedChange={() => handleClientToggle(client.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{client.name}</p>
                        <p className="text-xs text-slate-500 truncate">{client.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-2">No clients found</p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate('/clients')}
                  >
                    Add Clients
                  </Button>
                </div>
              )}
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-[#4F46E5]">{selectedClients.length}</span> clients selected
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Invoice Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Template & Dates */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#4F46E5]" />
                  Invoice Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="form-group">
                  <Label>Template (Optional)</Label>
                  <Select value={selectedTemplate || "none"} onValueChange={(val) => setSelectedTemplate(val === "none" ? "" : val)}>
                    <SelectTrigger data-testid="template-select">
                      <SelectValue placeholder="Select a template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template</SelectItem>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Issue Date</Label>
                    <Input
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      data-testid="issue-date-input"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      data-testid="due-date-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="dashboard-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Line Items (Same for all invoices)</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </CardHeader>
              <CardContent>
                <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th className="w-1/2">Description</th>
                      <th className="w-20">Qty</th>
                      <th className="w-28">Rate</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Input
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="Service or product description"
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          />
                        </td>
                        <td>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            className="h-8 w-8 p-0 hover:bg-rose-50"
                            disabled={formData.items.length === 1}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label>Tax Rate:</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                      className="w-20"
                    />
                    <span className="text-slate-500">%</span>
                  </div>
                  <div className="flex-1 text-right">
                    <span className="text-slate-600">Total per invoice: </span>
                    <span className="text-xl font-bold text-[#4F46E5]">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="text-lg">Notes (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes to include on all invoices..."
                  rows={3}
                />
              </CardContent>
            </Card>

            {/* Generate Button */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate('/invoices')}>
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || selectedClients.length === 0}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white min-w-[200px]"
                data-testid="generate-invoices-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Generate {selectedClients.length} Invoice{selectedClients.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
